import clsx from 'clsx';
import { AnimatePresence, m, LazyMotion, domAnimation } from 'framer-motion';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { LinkedinShareButton, TwitterShareButton } from 'react-share';
import { useCopyToClipboard } from 'react-use';

import Button from 'components/shared/button';
import LinkIcon from 'icons/link.inline.svg';
import LinkedInIcon from 'icons/linkedin.inline.svg';
import XIcon from 'icons/x-com.inline.svg';

const ANIMATION_DURATION = 0.2;
const MOTION_EASY = [0.25, 0.1, 0.25, 1];

const variants = {
  hidden: {
    opacity: 0,

    transition: {
      duration: ANIMATION_DURATION,
      ease: MOTION_EASY,
    },
  },
  visible: {
    opacity: 1,

    transition: {
      duration: ANIMATION_DURATION,
      ease: MOTION_EASY,
    },
  },
};

const SocialShare = ({ title, url, className: additionalClassName, offsetTop, isSticky }) => {
  const [isCopied, setIsCopied] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [_, copyToClipboard] = useCopyToClipboard();

  const handleCopy = () => {
    if (!isCopied) {
      copyToClipboard(url, { onCopy: setIsCopied(true) });
    }
  };

  useEffect(() => {
    if (isCopied) {
      setTimeout(() => {
        setIsCopied(false);
      }, 1500);
    }
  }, [isCopied]);

  return (
    <div className={clsx(additionalClassName, { 'h-full': isSticky })}>
      <LazyMotion features={domAnimation}>
        <m.div
          className={clsx(
            'flex items-center',
            isSticky ? 'sticky top-12 flex-col space-y-5 md:space-y-3' : 'space-x-5 md:space-x-3'
          )}
          initial={isSticky && 'hidden'}
          animate={offsetTop && 'visible'}
          variants={variants}
          style={offsetTop ? { marginTop: `${offsetTop}px` } : null}
        >
          <TwitterShareButton className="group" url={url} title={title} via="revenuecat">
            <XIcon className="h-5 text-gray-8 transition-colors duration-200 group-hover:text-gray-9" />
          </TwitterShareButton>

          <LinkedinShareButton className="group" url={url}>
            <LinkedInIcon className="h-5 text-gray-8 transition-colors duration-200 group-hover:text-gray-9" />
          </LinkedinShareButton>

          <Button className="group relative" onClick={handleCopy}>
            <AnimatePresence>
              {isCopied && (
                <m.span
                  className="absolute top-full left-1/2 mt-2 -translate-x-1/2 rounded bg-black py-1 px-1.5 text-xs font-normal capitalize text-white opacity-50 md:-ml-2"
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={variants}
                >
                  Copied!
                </m.span>
              )}
            </AnimatePresence>

            <LinkIcon className="h-5 text-gray-8 transition-colors duration-200 group-hover:text-gray-9" />
          </Button>
        </m.div>
      </LazyMotion>
    </div>
  );
};

SocialShare.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  isSticky: PropTypes.bool,
  offsetTop: PropTypes.number,
};

SocialShare.defaultProps = {
  className: null,
  isSticky: false,
  offsetTop: 0,
};

export default SocialShare;
